<template>
  <div v-if="fields.length" class="mt-1 space-y-1 w-full min-w-0">
    <div
      v-if="basePrice > 0.005 && fieldsTotal > 0.005"
      class="grid grid-cols-[minmax(0,1fr)_auto] gap-x-2 text-xs text-muted"
    >
      <span>Preț bază</span>
      <span class="tabular-nums text-toned whitespace-nowrap text-right">{{ formatPrice(basePrice) }}</span>
    </div>
    <ul class="space-y-1.5">
      <li
        v-for="row in fields"
        :key="`${row.fieldId}-${row.name}-${String(row.value)}`"
        class="text-xs text-muted min-w-0"
      >
        <div
          v-if="isSvgFile(row)"
          class="grid grid-cols-[minmax(0,1fr)_auto] gap-x-2 items-center"
        >
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
            <span class="shrink-0">{{ row.name }}:</span>
            <a
              :href="mediaUrl(String(row.value || ''))"
              target="_blank"
              rel="noopener noreferrer"
              download
              class="text-primary-600 hover:underline font-medium"
            >
              Descarcă SVG
            </a>
          </div>
          <span
            v-if="priceOf(row) > 0"
            class="tabular-nums text-toned whitespace-nowrap text-right"
          >
            +{{ formatPrice(priceOf(row)) }}
          </span>
        </div>
        <div
          v-else-if="isImage(row)"
          class="grid grid-cols-[minmax(0,1fr)_auto] gap-x-2 items-start"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span class="shrink-0">{{ row.name }}:</span>
            <a
              :href="mediaUrl(String(row.value || ''))"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex shrink-0 overflow-hidden rounded border border-default bg-muted"
            >
              <AppImage
                :src="String(row.value)"
                :alt="row.name"
                preset="thumbnail"
                width="32"
                height="32"
                sizes="32px"
                class="h-8 w-8 object-cover"
                loading="lazy"
              />
            </a>
          </div>
          <span
            v-if="priceOf(row) > 0"
            class="tabular-nums text-toned whitespace-nowrap text-right"
          >
            +{{ formatPrice(priceOf(row)) }}
          </span>
        </div>
        <div
          v-else-if="compact"
          class="min-w-0 space-y-0.5"
        >
          <p class="break-words leading-snug">
            {{ row.name }}: {{ formatCustomFieldValue(row.value, row.type) }}
            <span v-if="unitHint(row)" class="text-dimmed"> · {{ unitHint(row) }}</span>
          </p>
          <p
            v-if="priceOf(row) > 0"
            class="tabular-nums text-toned text-right whitespace-nowrap"
          >
            +{{ formatPrice(priceOf(row)) }}
          </p>
        </div>
        <div
          v-else
          class="grid grid-cols-[minmax(0,1fr)_auto] gap-x-2 items-start"
        >
          <span class="min-w-0 break-words leading-snug">
            {{ row.name }}: {{ formatCustomFieldValue(row.value, row.type) }}
            <span v-if="unitHint(row)" class="text-dimmed"> · {{ unitHint(row) }}</span>
          </span>
          <span
            v-if="priceOf(row) > 0"
            class="tabular-nums text-toned whitespace-nowrap text-right"
          >
            +{{ formatPrice(priceOf(row)) }}
          </span>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import {
  formatCustomFieldValue,
  getFieldUnitCount,
  isCustomFieldImageValue,
  type ComplexFieldType,
} from '~/utils/complexProductFields'
import { withoutHiddenFromCustomerFields } from '~/utils/orderCustomFields'

export interface CartPriceBreakdownField {
  fieldId: number
  name: string
  type: string
  value: unknown
  priceDelta?: number
  hideFromCustomer?: boolean
}

const props = withDefaults(defineProps<{
  unitPrice: number
  fields?: CartPriceBreakdownField[] | null
  /** Narrow sidebar: label on one line, price below (right-aligned). */
  compact?: boolean
  /** Hide fields marked hideFromCustomer (cart / checkout / customer views). */
  hideCustomerHiddenFields?: boolean
}>(), {
  compact: false,
  hideCustomerHiddenFields: false,
})

const { formatPrice } = useSeo()
const { mediaUrl } = useMediaUrl()

const fields = computed(() => {
  const list = props.fields || []
  return props.hideCustomerHiddenFields ? withoutHiddenFromCustomerFields(list) : list
})

const fieldsTotal = computed(() =>
  fields.value.reduce((sum, row) => sum + (Number(row.priceDelta) || 0), 0),
)

const basePrice = computed(() => Math.max(0, Number(props.unitPrice) - fieldsTotal.value))

function priceOf(row: CartPriceBreakdownField) {
  return Number(row.priceDelta) || 0
}

function isImage(row: CartPriceBreakdownField) {
  return isCustomFieldImageValue(row.value, row.type)
}

function isSvgFile(row: CartPriceBreakdownField) {
  if (row.type !== 'file') return false
  return /\.svg(\?.*)?$/i.test(String(row.value || ''))
}

function unitHint(row: CartPriceBreakdownField) {
  if (row.type === 'package_choice') return ''
  const units = getFieldUnitCount(row.type as ComplexFieldType, row.value)
  if (units <= 0) return ''
  if (row.type === 'date_range') return units === 1 ? '1 zi' : `${units} zile`
  if (row.type === 'number') return units === 1 ? '1 unitate' : `${units} unități`
  return ''
}
</script>
